import { POSITIONING_VARIANT_STORAGE_KEY, type PositioningVariant } from '@/constants/funnel';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useFunnelLead } from '@/hooks/useFunnelLead';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JoinFunnelLayout } from '../JoinFunnelLayout';
import { FunnelField } from '../components/FunnelField';
import { ValuePropCard } from '../components/ValuePropCard';
import { FUNNEL_COPY } from '../funnel-copy';
import { stepRoutePath } from '../routes';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

function readVariant(): PositioningVariant {
  const stored = sessionStorage.getItem(POSITIONING_VARIANT_STORAGE_KEY);
  if (stored === 'test-before-test' || stored === 'checkup' || stored === 'default') {
    return stored;
  }
  return 'default';
}

export function JoinStep1() {
  const variant = readVariant();
  const copy = FUNNEL_COPY[variant].step1;
  const navigate = useNavigate();
  const { lead, updateLead } = useFunnelLead();
  const analytics = useAnalyticsCalls();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Hydrate from server lead exactly once when it lands.
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (!lead || hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    setFirstName(lead.firstName ?? '');
    setLastName(lead.lastName ?? '');
    setEmail(lead.email ?? '');
  }, [lead]);

  // Onboarding - Started (Meta view-002 per Tracking Requirements) fires on
  // first mount only. The per-step page view is handled globally by
  // useAnalyticsPageViews, which now attaches `positioning_variant` and
  // `funnel_step` to `[Amplitude] Page Viewed` for /join routes.
  const hasFiredOnceRef = useRef(false);
  useEffect(() => {
    if (hasFiredOnceRef.current) return;
    hasFiredOnceRef.current = true;
    analytics.callOnboardingStarted();
  }, [analytics]);

  const handleFieldBlur = async (
    field: 'first_name' | 'last_name' | 'email',
    value: string,
  ) => {
    if (!value) return;
    if (field === 'email' && !EMAIL_REGEX.test(value)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    try {
      await updateLead(
        field === 'first_name'
          ? { firstName: value }
          : field === 'last_name'
            ? { lastName: value }
            : { email: value },
      );
      analytics.callFunnelFieldCaptured({ field, value, positioningVariant: variant });
    } catch (err) {
      // PATCH errors aren't surfaced inline — surface email format failures
      // (the only blocking validation) up via emailError, log everything else.
      if (field === 'email') {
        setEmailError('We couldn\'t save your email. Try again.');
      }
    }
  };

  const isComplete = firstName.trim() && lastName.trim() && email.trim() && !emailError;

  const handleContinue = () => {
    if (!isComplete) return;
    navigate(stepRoutePath(variant, 2));
  };

  return (
    <JoinFunnelLayout
      step={1}
      onContinue={handleContinue}
      isContinueDisabled={!isComplete}
    >
      <div className="flex flex-col gap-3">
        <h1 className="text-3xl font-bold leading-tight text-catch-dark-blue-90">
          {copy.header}
        </h1>
        <p className="text-base text-gray-500">{copy.subheader}</p>
      </div>

      <div className="flex flex-col gap-4">
        <FunnelField
          id="first-name"
          label="Your name"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onBlur={(e) => handleFieldBlur('first_name', e.target.value.trim())}
          autoComplete="given-name"
        />
        <FunnelField
          id="last-name"
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onBlur={(e) => handleFieldBlur('last_name', e.target.value.trim())}
          autoComplete="family-name"
        />
        <FunnelField
          id="email"
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          onBlur={(e) => handleFieldBlur('email', e.target.value.trim())}
          autoComplete="email"
          error={emailError}
        />
      </div>

      <ValuePropCard copy={copy.valueProp} />
    </JoinFunnelLayout>
  );
}

export default JoinStep1;
